import {Injectable, Injector} from "@angular/core";

import {ReportError} from "app/app.service";
import {BaseListService, BatchResponse} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";

import {Lookup} from "framework/services/utils.service";

@Injectable()
export class TemplatesListService extends BaseListService<Models.AutomationTemplateListRequest, Models.TemplateBatchRequest, Models.Template>
{
    constructor(inj: Injector)
    {
        super(inj, Models.Template);
    }

    @ReportError
    public list(listReq: Models.AutomationTemplateListRequest): Promise<Models.ListResponse>
    {
        return this.app.domain.apis.templates.listTemplatesV2(listReq);
    }

    @ReportError
    public getTemplate(templateId: string): Promise<Models.Template>
    {
        return this.app.domain.apis.templates.v2GetTemplate(templateId);
    }

    @ReportError
    public async resolveTemplateNames(templateIds: string[]): Promise<Map<string, string>>
    {
        const names = new Map<string, string>();
        const ids   = Array.from(new Set((templateIds || []).filter((id) => !!id)));
        if (!ids.length) return names;

        const lookup = await this.batchAsLookup(Models.TemplateBatchRequest.newInstance({ids: ids}));
        for (const id of ids)
        {
            const name = lookup[id]?.name;
            if (name) names.set(id, name);
        }

        return names;
    }

    public async resolveTemplateName(templateId: string): Promise<string>
    {
        if (!templateId) return "-";

        const names = await this.resolveTemplateNames([templateId]);
        return names.get(templateId) || templateId;
    }

    @ReportError
    public getParams(templateId: string,
                     req: Models.TemplateV2GetParamsRequest): Promise<Models.TemplateV2GetParamsResponse>
    {
        return this.app.domain.apis.templates.v2GetTemplateParams(templateId, req);
    }

    @ReportError
    public execute(templateId: string,
                   req: Models.TemplateV2ExecuteRequest): Promise<Models.TemplateV2ExecuteResponse>
    {
        return this.app.domain.apis.templates.v2ExecuteTemplate(templateId, req);
    }

    @ReportError
    public plan(templateId: string,
                req: Models.TemplatePlanRequest): Promise<Models.TemplatePlanResponse>
    {
        return this.app.domain.apis.templates.v2PlanTemplate(templateId, req);
    }

    @ReportError
    public commit(templateId: string,
                  req: Models.TemplateCommitRequest): Promise<Models.TemplateCommitResponse>
    {
        return this.app.domain.apis.templates.v2CommitTemplate(templateId, req);
    }

    @ReportError
    public scan(templateId: string,
                req: Models.TemplateScanRequest): Promise<Models.TemplateScanTriggerResponse>
    {
        return this.app.domain.apis.templates.v2ScanTemplate(templateId, req);
    }

    @ReportError
    public getScanResult(templateId: string,
                         scanId: string): Promise<Models.TemplateScanResultResponse>
    {
        return this.app.domain.apis.templates.v2GetTemplateScanResult(templateId, scanId);
    }

    @ReportError
    public deleteTemplate(templateId: string): Promise<boolean>
    {
        return this.app.domain.apis.templates.v2DeleteTemplate(templateId);
    }

    @ReportError
    public create(req: Models.CreateTemplateV2Request): Promise<Models.Template>
    {
        return this.app.domain.apis.templates.v2CreateTemplate(req);
    }

    @ReportError
    public update(templateId: string,
                  req: Models.UpdateTemplateV2Request): Promise<Models.Template>
    {
        return this.app.domain.apis.templates.v2UpdateTemplate(templateId, req);
    }

    @ReportError
    protected async getBatchResponseImpl(req: Models.TemplateBatchRequest): Promise<BatchResponse<Models.Template>>
    {
        if (!req?.ids?.length) return {results: {}};

        const templates = await this.app.domain.apis.templates.batchTemplate(Models.TemplateBatchRequest.newInstance({ids: req.ids}));

        const results: Lookup<Models.Template> = {};
        for (const template of templates)
        {
            if (template) results[template.template_id] = template;
        }

        return {results: results};
    }

    protected createBatchRequest(listReq: Models.AutomationTemplateListRequest,
                                 ids: string[]): Models.TemplateBatchRequest
    {
        return Models.TemplateBatchRequest.newInstance({ids: ids ?? []});
    }
}
